import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog,
    DialogContent,
    Typography,
    Paper,
    Grid,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    TextField,
    Card,
    Button,
    Icon,
    LinearProgress,
    CircularProgress,
    IconButton,
    Divider,
    Popover,
    Box
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import * as Actions from '../../store/actions';

const useStyles = makeStyles(theme => ({
    dialogPaper: {
        minHeight: '70vh',
        maxHeight: '90vh',
        position: 'relative'
    },
    metricCard: {
        textAlign: 'center',
        padding: theme.spacing(2)
    },
    configSection: {
        marginBottom: theme.spacing(2)
    },
    runningIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: '#ff9800'
    },
    runningIndicatorContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        padding: theme.spacing(0.5, 2),
        borderRadius: theme.spacing(1),
        fontSize: '0.875rem'
    },
    multipleTestsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        maxWidth: '400px',
        overflowX: 'auto',
        overflowY: 'hidden',
        '&::-webkit-scrollbar': {
            height: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.3)',
            borderRadius: '2px',
        }
    },
    singleTestIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        padding: theme.spacing(0.5, 1.5),
        borderRadius: theme.spacing(0.5),
        fontSize: '0.8rem',
        minWidth: '160px',
        maxWidth: '180px',
        whiteSpace: 'nowrap',
        flexShrink: 0
    },
    testTextContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center'
    },
    runningSpinner: {
        color: 'white',
        animation: '$spin 2s linear infinite'
    },
    runningText: {
        fontWeight: 'bold',
        fontSize: '0.875rem'
    },
    runningSubtext: {
        fontSize: '0.75rem',
        opacity: 0.8
    },
    '@keyframes spin': {
        '0%': {
            transform: 'rotate(0deg)'
        },
        '100%': {
            transform: 'rotate(360deg)'
        }
    },
    errorText: {
        color: theme.palette.error.main
    },
    successText: {
        color: theme.palette.success.main
    },
    popover: {
        pointerEvents: 'none'
    },
    popoverPaper: {
        padding: theme.spacing(2),
        maxWidth: 300,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`
    },
    infoIcon: {
        fontSize: '16px',
        color: theme.palette.text.secondary,
        cursor: 'pointer',
        marginLeft: theme.spacing(1),
        '&:hover': {
            color: theme.palette.primary.main
        }
    },
    dialogHeader: {
        backgroundColor: theme.palette.grey[100],
        padding: theme.spacing(1, 2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`,
        minHeight: '48px',
        gap: theme.spacing(2)
    },
    closeButton: {
        padding: theme.spacing(1),
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        }
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1302,
        backdropFilter: 'blur(2px)'
    },
    loadingContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(2)
    }
}));

const MemoryBandwidthDialog = ({ open, onClose, clusterId, nodes = [] }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    
    // Redux state
    const clusterState = useSelector(({ clusters }) => clusters?.cluster || {});
    const {
        stressTests = {
            data: null,
            history: null,
            loading: false,
            error: null
        },
        clusterMetrics = { data: null }
    } = clusterState;
    
    const {
        data: stressTestData,
        history: stressTestHistory,
        loading: stressTestLoading,
        error: stressTestError
    } = stressTests;
    
    
    const { data: metricsData } = clusterMetrics;

    // Local state
    const [selectedNode, setSelectedNode] = useState('');
    const [testDuration, setTestDuration] = useState(60);
    const [testIntensity, setTestIntensity] = useState('medium');
    const [testPattern, setTestPattern] = useState('sequential');
    const [bufferSize, setBufferSize] = useState('1G');
    const [threadCount, setThreadCount] = useState('auto');
    const [runningTests, setRunningTests] = useState(new Map()); // Map of nodeId -> {nodeName, startTime, testConfig}
    const [connectionStatus, setConnectionStatus] = useState('connected');
    const [pollingErrors, setPollingErrors] = useState(0);
    const [lastDataUpdate, setLastDataUpdate] = useState(Date.now());
    const [nodeDataLoading, setNodeDataLoading] = useState(false);
    
    // Popover state
    const [anchorEl, setAnchorEl] = useState(null);
    const [activePopover, setActivePopover] = useState(null);
    
    // Ref to track if component is mounted
    const isMountedRef = useRef(true);
    
    // Animation state for smooth gauge transitions
    const [animatedMetrics, setAnimatedMetrics] = useState({
        addBandwidth: 0,
        copyBandwidth: 0,
        scaleBandwidth: 0,
        triadBandwidth: 0,
        averageBandwidth: 0,
        readBandwidth: 0,
        writeBandwidth: 0,
        totalBandwidth: 0,
        latency: 0
    });
    const animationFrameRef = useRef(null);
    const animationStartTime = useRef(null);

    // Configuration options
    const testPatterns = [
        { value: 'sequential', label: 'Sequential Read/Write', description: 'Linear memory access pattern' },
        { value: 'random', label: 'Random Access', description: 'Random memory access pattern' },
        { value: 'streaming', label: 'Streaming (STREAM)', description: 'STREAM benchmark pattern' },
        { value: 'mixed', label: 'Mixed Pattern', description: 'Combination of access patterns' }
    ];
    
    const bufferSizes = ['256M', '512M', '1G', '2G', '4G'];
    
    const threadCounts = [
        { value: 'auto', label: 'Auto (CPU cores)' },
        { value: '1', label: '1 Thread' },
        { value: '2', label: '2 Threads' },
        { value: '4', label: '4 Threads' },
        { value: '8', label: '8 Threads' },
        { value: '16', label: '16 Threads' }
    ];

    const intensityLevels = [
        { value: 'light', label: 'Light (25%)', description: 'Low memory pressure, safe for production' },
        { value: 'medium', label: 'Medium (50%)', description: 'Moderate memory bandwidth usage' },
        { value: 'heavy', label: 'Heavy (75%)', description: 'High memory bandwidth usage' },
        { value: 'maximum', label: 'Maximum (100%)', description: 'Maximum memory bandwidth stress' }
    ];

    // Load data when dialog opens
    useEffect(() => {
        isMountedRef.current = true;
        
        if (open && clusterId) {
            setConnectionStatus('connected');
            setPollingErrors(0);
            
            // Fetch available nodes from cluster volumes
            dispatch(Actions.fetchClusterVolumes(clusterId));
            
            const initialParams = { 
                testType: 'memory_bandwidth', 
                limit: 50,
                _cacheBust: Date.now()
            };
            dispatch(Actions.fetchStressTestHistory(clusterId, initialParams));
        } else if (!open) {
            setRunningTests(new Map());
        }

        return () => {
            isMountedRef.current = false;
            if (!open) {
                setRunningTests(new Map());
            }
        };
    }, [open, clusterId, dispatch]);

    // Force refresh when node selection changes
    useEffect(() => {
        if (selectedNode && clusterId && open) {
            setNodeDataLoading(true);
            
            dispatch(Actions.setStressTestHistory(null));
            setLastDataUpdate(Date.now());
            
            const nodeChangeParams = { 
                testType: 'memory_bandwidth', 
                limit: 50,
                nodes: [selectedNode],
                _cacheBust: Date.now()
            };
            
            dispatch(Actions.fetchStressTestHistory(clusterId, nodeChangeParams))
                .finally(() => {
                    if (isMountedRef.current) {
                        setNodeDataLoading(false);
                    }
                });
        } else {
            dispatch(Actions.setStressTestHistory(null));
            setNodeDataLoading(false);
        }
    }, [selectedNode, clusterId, open, dispatch]);

    // Force component re-render when stress test history changes
    useEffect(() => {
        
        if (stressTestHistory && selectedNode) {
            const historyData = stressTestHistory?.data?.history || stressTestHistory?.history;
            
            let nodeRecords = [];
            if (historyData?.[selectedNode] && Array.isArray(historyData[selectedNode])) {
                nodeRecords = historyData[selectedNode];
            } else if (Array.isArray(historyData)) {
                nodeRecords = historyData.filter(record => 
                    record.hostname === selectedNode || record.node === selectedNode
                );
            }
                  
            setLastDataUpdate(Date.now());
        } else if (stressTestHistory && !selectedNode) {
            setLastDataUpdate(Date.now());
        }
    }, [stressTestHistory, selectedNode]);

    // Get available nodes from props (passed from StressTestPanel)
    const getAvailableNodes = () => {
        if (!nodes || nodes.length === 0) {
            return [];
        }
        
        
        const availableNodes = nodes
            .filter(node => {
                const isAvailable = node.state?.state === 'Available' || node.state?.state === 'ready' || node.state?.state === 'online' || !node.state;
                return isAvailable;
            })
            .map(node => node.name)
            .sort();
            
        return availableNodes;
    };

    // Popover handlers
    const handlePopoverOpen = (event, popoverId) => {
        setAnchorEl(event.currentTarget);
        setActivePopover(popoverId);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setActivePopover(null);
    };

    // Manual refresh function
    const handleManualRefresh = async () => {
        if (!selectedNode) return;

        try {
            await dispatch(Actions.fetchStressTestHistory(clusterId, { 
                testType: 'memory_bandwidth', 
                limit: 50,
                nodes: selectedNode ? [selectedNode] : undefined,
                _cacheBust: Date.now()
            }));
            
            if (isMountedRef.current && connectionStatus !== 'connected') {
                setConnectionStatus('connected');
                setPollingErrors(0);
            }
            
            if (isMountedRef.current) {
                setLastDataUpdate(Date.now());
            }
        } catch (error) {
            console.error('Manual refresh failed:', error);
            
            if (isMountedRef.current) {
                setPollingErrors(prev => prev + 1);
                if (pollingErrors >= 3) {
                    setConnectionStatus('failed');
                }
            }
        }
    };

    // Animation function for smooth gauge transitions
    const animateToNewValues = (targetMetrics, duration = 2000) => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        const startValues = { ...animatedMetrics };
        animationStartTime.current = performance.now();
        
        const animate = (currentTime) => {
            if (!isMountedRef.current) return;
            
            const elapsed = currentTime - animationStartTime.current;
            const progress = Math.min(elapsed / duration, 1);
            
            // Different easing for increasing vs decreasing values (like car tachometer)
            const isDecreasing = Object.keys(targetMetrics).some(key => 
                targetMetrics[key] < startValues[key]
            );
            
            let easedProgress;
            if (isDecreasing) {
                // Smooth deceleration for decreasing values (like releasing gas pedal)
                easedProgress = 1 - Math.pow(1 - progress, 3); // Cubic ease-out
            } else {
                // Slightly bouncy for increasing values (like pressing gas pedal)
                easedProgress = progress < 0.5 
                    ? 2 * progress * progress 
                    : 1 - Math.pow(-2 * progress + 2, 2) / 2; // Ease-in-out
            }
            
            const newAnimatedMetrics = {};
            Object.keys(targetMetrics).forEach(key => {
                const start = startValues[key] || 0;
                const end = targetMetrics[key];
                newAnimatedMetrics[key] = start + (end - start) * easedProgress;
            });
            
            setAnimatedMetrics(newAnimatedMetrics);
            
            if (progress < 1) {
                animationFrameRef.current = requestAnimationFrame(animate);
            }
        };
        
        animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Execute memory bandwidth test
    const handleExecuteTest = async () => {
        if (!selectedNode) return;

        if (runningTests.has(selectedNode)) return;

        const validDuration = typeof testDuration === 'number' && testDuration >= 10 && testDuration <= 600 ? testDuration : 60;
        const validThreadCount = threadCount === 'auto' ? 'auto' : parseInt(threadCount);

        const testConfig = {
            testType: 'memory_bandwidth',
            nodes: [selectedNode],
            duration: validDuration,
            intensity: testIntensity,
            options: {
                pattern: testPattern,
                bufferSize: bufferSize,
                threadCount: validThreadCount
            }
        };

        const newRunningTests = new Map(runningTests);
        newRunningTests.set(selectedNode, {
            nodeName: selectedNode,
            startTime: new Date(),
            testConfig: testConfig,
            connectionStatus: 'connected'
        });
        setRunningTests(newRunningTests);

        try {
            setPollingErrors(0);
            setConnectionStatus('connected');
            
            dispatch(Actions.clearStressTestData());

            // Animate gauges to 0 quickly when starting new test
            animateToNewValues({
                addBandwidth: 0,
                copyBandwidth: 0,
                scaleBandwidth: 0,
                triadBandwidth: 0,
                averageBandwidth: 0,
                readBandwidth: 0,
                writeBandwidth: 0,
                totalBandwidth: 0,
                latency: 0
            }, 800); // Fast 0.8 second animation to 0

            const result = await dispatch(Actions.executeStressTest(clusterId, testConfig));
            
            if (result.status === 'success') {
                
                // Start polling for results
                const startPolling = () => {
                    const pollResults = async () => {
                        if (!isMountedRef.current || !runningTests.has(selectedNode)) {
                            return;
                        }

                        try {
                            await dispatch(Actions.fetchStressTestHistory(clusterId, {
                                testType: 'memory_bandwidth',
                                limit: 10,
                                nodes: [selectedNode],
                                _cacheBust: Date.now()
                            }));

                            setLastDataUpdate(Date.now());

                            if (connectionStatus !== 'connected') {
                                setConnectionStatus('connected');
                                setPollingErrors(0);
                            }
                        } catch (error) {
                            console.error('Polling error:', error);
                            const newErrors = pollingErrors + 1;
                            setPollingErrors(newErrors);

                            if (newErrors >= 3) {
                                setConnectionStatus('failed');
                            } else if (newErrors >= 2) {
                                setConnectionStatus('degraded');
                            }
                        }

                        if (isMountedRef.current && runningTests.has(selectedNode)) {
                            setTimeout(pollResults, connectionStatus === 'failed' ? 10000 : 3000);
                        }
                    };

                    setTimeout(pollResults, 3000);
                };

                startPolling();

                // Auto-complete test after duration + buffer time
                setTimeout(() => {
                    if (isMountedRef.current) {
                        setRunningTests(prev => {
                            const updated = new Map(prev);
                            updated.delete(selectedNode);
                            return updated;
                        });
                        
                        handleManualRefresh();
                    }
                }, (validDuration + 30) * 1000);
            }
        } catch (error) {
            console.error('Memory bandwidth test execution failed:', error);
            
            setRunningTests(prev => {
                const updated = new Map(prev);
                updated.delete(selectedNode);
                return updated;
            });
        }
    };

    // Get current memory bandwidth metrics
    const getCurrentMetrics = () => {
        if (!selectedNode) {
            return { 
                readBandwidth: 0, 
                writeBandwidth: 0, 
                totalBandwidth: 0, 
                latency: 0, 
                isFromTest: false 
            };
        }
        
        const historyData = stressTestHistory?.data?.history || stressTestHistory?.history;
        
        if (historyData && selectedNode) {
            let nodeRecords = [];
            if (historyData[selectedNode] && Array.isArray(historyData[selectedNode])) {
                nodeRecords = historyData[selectedNode];
            } else if (Array.isArray(historyData)) {
                nodeRecords = historyData.filter(record => 
                    record.hostname === selectedNode || record.node === selectedNode
                );
            }

            if (nodeRecords.length > 0) {
                const latestTest = nodeRecords[nodeRecords.length - 1];
                const results = latestTest.results || {};
                
                const testTime = moment(latestTest.timestamp);
                const now = moment();
                const minutesAgo = now.diff(testTime, 'minutes');
                
                // Map memory bandwidth results to display format
                // Memory bandwidth tests return: addBandwidth, copyBandwidth, scaleBandwidth, triadBandwidth, averageBandwidth
                const metrics = {
                    // Use STREAM benchmark results for memory bandwidth
                    addBandwidth: results.addBandwidth || 0,
                    copyBandwidth: results.copyBandwidth || 0,
                    scaleBandwidth: results.scaleBandwidth || 0,
                    triadBandwidth: results.triadBandwidth || 0,
                    averageBandwidth: results.averageBandwidth || 0,
                    // Legacy support for other test types
                    readBandwidth: results.readBandwidth || results.read_bandwidth || 0,
                    writeBandwidth: results.writeBandwidth || results.write_bandwidth || 0,
                    totalBandwidth: results.totalBandwidth || results.total_bandwidth || 
                                   results.averageBandwidth || // Use average for memory tests
                                   ((results.readBandwidth || 0) + (results.writeBandwidth || 0)) || 0,
                    latency: results.latency || results.average_latency || 0,
                    isFromTest: true,
                    dataAge: minutesAgo < 60 ? minutesAgo : null
                };
                                
                return metrics;
            }
        }
        
        // Fallback to live metrics if available
        if (metricsData?.nodeMetrics && selectedNode) {
            const nodeMetrics = metricsData.nodeMetrics.find(node => 
                node.hostname === selectedNode || node.nodeName === selectedNode
            );
            
            if (nodeMetrics?.system?.memory) {
                const memoryMetrics = nodeMetrics.system.memory;
                return {
                    readBandwidth: memoryMetrics.readBandwidth || 0,
                    writeBandwidth: memoryMetrics.writeBandwidth || 0,
                    totalBandwidth: memoryMetrics.totalBandwidth || 0,
                    latency: memoryMetrics.latency || 0,
                    isFromTest: false
                };
            }
        }

        return { 
            readBandwidth: 0, 
            writeBandwidth: 0, 
            totalBandwidth: 0, 
            latency: 0, 
            isFromTest: false 
        };
    };

    const currentMetrics = React.useMemo(() => {
        return getCurrentMetrics();
    }, [stressTestHistory, selectedNode, metricsData, lastDataUpdate]);
    
    // Initialize animated metrics with current values on first load
    useEffect(() => {
        if (open && currentMetrics && isMountedRef.current) {
            // Only initialize if animated metrics are at zero (first time)
            const isFirstLoad = animatedMetrics.totalBandwidth === 0 && 
                               animatedMetrics.averageBandwidth === 0 && 
                               animatedMetrics.addBandwidth === 0;
                               
            if (isFirstLoad) {
                setAnimatedMetrics({
                    addBandwidth: currentMetrics.addBandwidth,
                    copyBandwidth: currentMetrics.copyBandwidth,
                    scaleBandwidth: currentMetrics.scaleBandwidth,
                    triadBandwidth: currentMetrics.triadBandwidth,
                    averageBandwidth: currentMetrics.averageBandwidth,
                    readBandwidth: currentMetrics.readBandwidth,
                    writeBandwidth: currentMetrics.writeBandwidth,
                    totalBandwidth: currentMetrics.totalBandwidth,
                    latency: currentMetrics.latency
                });
            }
        }
    }, [open, currentMetrics]);

    // Animation effect - watch for metrics changes and animate to new values
    useEffect(() => {
        const metrics = getCurrentMetrics();
        
        // Only animate if we have meaningful changes and component is mounted
        if (isMountedRef.current) {
            const hasSignificantChange = 
                Math.abs(metrics.averageBandwidth - animatedMetrics.averageBandwidth) > 10 ||
                Math.abs(metrics.addBandwidth - animatedMetrics.addBandwidth) > 5 ||
                Math.abs(metrics.copyBandwidth - animatedMetrics.copyBandwidth) > 5 ||
                Math.abs(metrics.scaleBandwidth - animatedMetrics.scaleBandwidth) > 5 ||
                Math.abs(metrics.triadBandwidth - animatedMetrics.triadBandwidth) > 5 ||
                Math.abs(metrics.totalBandwidth - animatedMetrics.totalBandwidth) > 10 ||
                Math.abs(metrics.latency - animatedMetrics.latency) > 0.1;

            if (hasSignificantChange) {
                // Different durations: faster for going to 0, normal speed for increasing
                const isResetting = metrics.averageBandwidth === 0 && metrics.totalBandwidth === 0;
                const duration = isResetting ? 600 : 1200; // Much faster for reset (0.6s), slower for increases (1.2s)
                
                animateToNewValues({
                    addBandwidth: metrics.addBandwidth,
                    copyBandwidth: metrics.copyBandwidth,
                    scaleBandwidth: metrics.scaleBandwidth,
                    triadBandwidth: metrics.triadBandwidth,
                    averageBandwidth: metrics.averageBandwidth,
                    readBandwidth: metrics.readBandwidth,
                    writeBandwidth: metrics.writeBandwidth,
                    totalBandwidth: metrics.totalBandwidth,
                    latency: metrics.latency
                }, duration);
            }
        }
    }, [lastDataUpdate, selectedNode]); // Depend on data update triggers

    // Cleanup animation on unmount
    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);
    
    const availableNodes = getAvailableNodes();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            classes={{ paper: classes.dialogPaper }}
            BackdropProps={{
                style: {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1300
                }
            }}
            PaperProps={{
                style: {
                    zIndex: 1301
                }
            }}
        >
            {/* Loading Overlay */}
            {nodeDataLoading && (
                <div className={classes.loadingOverlay}>
                    <div className={classes.loadingContent}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" color="textSecondary">
                            Loading node data...
                        </Typography>
                    </div>
                </div>
            )}
            
            {/* Custom Header Bar */}
            <div className={classes.dialogHeader}>
                <div className="flex items-center">
                    <Icon className="mr-8 text-purple">memory</Icon>
                    <Typography variant="h6">
                        Memory Bandwidth Test
                        {runningTests.size > 0 && (
                            <span style={{ color: '#1976d2', marginLeft: '8px', fontSize: '0.8em' }}>
                                ({runningTests.size} running)
                            </span>
                        )}
                    </Typography>
                    
                    {runningTests.size > 0 && connectionStatus !== 'connected' && (
                        <Icon 
                            className="ml-8" 
                            color={connectionStatus === 'degraded' ? 'warning' : 'error'}
                            title={connectionStatus === 'degraded' ? 
                                'Connection issues - using reduced refresh rate' : 
                                'Connection failed - real-time updates unavailable'
                            }
                        >
                            {connectionStatus === 'degraded' ? 'signal_wifi_4_bar' : 'signal_wifi_off'}
                        </Icon>
                    )}
                </div>
                
                <div className="flex items-center gap-4">
                    {runningTests.size > 0 && (
                        <div className={classes.multipleTestsContainer}>
                            {Array.from(runningTests.entries()).map(([nodeId, testInfo]) => (
                                <div key={nodeId} className={classes.singleTestIndicator}>
                                    <CircularProgress 
                                        size={14} 
                                        className={classes.runningSpinner}
                                    />
                                    <div className={classes.testTextContainer}>
                                        <div className={classes.runningText}>
                                            {testInfo.nodeName}
                                        </div>
                                        <div className={classes.runningSubtext}>
                                            {testInfo.testConfig.options.pattern} • {testInfo.testConfig.duration}s
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    <IconButton 
                        className={classes.closeButton}
                        onClick={onClose}
                        size="small"
                    >
                        <Icon>close</Icon>
                    </IconButton>
                </div>
            </div>

            <DialogContent>
                <Grid container spacing={3}>
                    {/* Configuration Section */}
                    <Grid item xs={12} md={4}>
                        <Paper className="p-16">
                            <Typography variant="h6" className="mb-16">
                                Test Configuration
                            </Typography>

                            <div className={classes.configSection}>
                                <FormControl fullWidth size="small" margin="normal">
                                    <InputLabel>Target Node</InputLabel>
                                    <Select
                                        value={selectedNode}
                                        onChange={(e) => setSelectedNode(e.target.value)}
                                        label="Target Node"
                                    >
                                        {availableNodes.map(nodeName => (
                                            <MenuItem key={nodeName} value={nodeName}>
                                                {nodeName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>

                            <Divider className="my-16" />

                            <div className={classes.configSection}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Duration (seconds)"
                                            type="number"
                                            value={testDuration}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === '') {
                                                    setTestDuration('');
                                                } else {
                                                    const numValue = parseInt(value);
                                                    if (!isNaN(numValue)) {
                                                        setTestDuration(numValue);
                                                    }
                                                }
                                            }}
                                            onBlur={(e) => {
                                                const value = e.target.value;
                                                if (value === '' || isNaN(parseInt(value))) {
                                                    setTestDuration(60);
                                                } else {
                                                    const numValue = parseInt(value);
                                                    setTestDuration(Math.min(600, Math.max(10, numValue)));
                                                }
                                            }}
                                            inputProps={{ min: 10, max: 600 }}
                                            helperText="Range: 10-600 seconds"
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Buffer Size</InputLabel>
                                            <Select
                                                value={bufferSize}
                                                onChange={(e) => setBufferSize(e.target.value)}
                                                label="Buffer Size"
                                            >
                                                {bufferSizes.map(size => (
                                                    <MenuItem key={size} value={size}>{size}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Memory Pattern</InputLabel>
                                            <Select
                                                value={testPattern}
                                                onChange={(e) => setTestPattern(e.target.value)}
                                                label="Memory Pattern"
                                            >
                                                {testPatterns.map(pattern => (
                                                    <MenuItem key={pattern.value} value={pattern.value}>
                                                        <div>
                                                            <Typography variant="body2">{pattern.label}</Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {pattern.description}
                                                            </Typography>
                                                        </div>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Thread Count</InputLabel>
                                            <Select
                                                value={threadCount}
                                                onChange={(e) => setThreadCount(e.target.value)}
                                                label="Thread Count"
                                            >
                                                {threadCounts.map(count => (
                                                    <MenuItem key={count.value} value={count.value}>
                                                        {count.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Test Intensity</InputLabel>
                                                <Select
                                                    value={testIntensity}
                                                    onChange={(e) => setTestIntensity(e.target.value)}
                                                    label="Test Intensity"
                                                >
                                                    {intensityLevels.map(level => (
                                                        <MenuItem key={level.value} value={level.value}>
                                                            {level.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <Icon 
                                                className={classes.infoIcon}
                                                style={{ marginBottom: '8px' }}
                                                onMouseEnter={(e) => handlePopoverOpen(e, 'intensity-info')}
                                                onMouseLeave={handlePopoverClose}
                                            >
                                                info
                                            </Icon>
                                            <Popover
                                                className={classes.popover}
                                                classes={{ paper: classes.popoverPaper }}
                                                open={Boolean(anchorEl) && activePopover === 'intensity-info'}
                                                anchorEl={anchorEl}
                                                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                                                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                                onClose={handlePopoverClose}
                                                disableAutoFocus={true}
                                                disableEnforceFocus={true}
                                                disableRestoreFocus={true}
                                            >
                                                <div>
                                                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                                        Memory Bandwidth Test Intensity
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginBottom: '6px' }}>
                                                        <strong>Light (25%):</strong> Single-threaded, small buffers - Safe for production
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginBottom: '6px' }}>
                                                        <strong>Medium (50%):</strong> Multi-threaded, moderate buffers
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginBottom: '6px' }}>
                                                        <strong>Heavy (75%):</strong> High thread count, large buffers
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>Maximum (100%):</strong> Maximum threads, maximum buffer size
                                                    </Typography>
                                                </div>
                                            </Popover>
                                        </div>
                                    </Grid>
                                </Grid>
                            </div>
                        </Paper>
                    </Grid>

                    {/* Memory Bandwidth Display */}
                    <Grid item xs={12} md={8}>
                        <Paper className="p-16 -mt-20" style={{ marginTop: '24px' }}>
                            <Grid container spacing={3}>
                                {/* Main Bandwidth Gauge */}
                                <Grid item xs={12} md={6}>
                                    <Card className="p-16 text-center" style={{ minHeight: '212px' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '16px' }}>
                                            <Typography variant="h6">Total Memory Bandwidth</Typography>
                                            <Typography variant="caption" style={{ 
                                                color: currentMetrics.isFromTest ? '#4caf50' : '#ff9800',
                                                fontSize: '10px',
                                                fontWeight: 'bold'
                                            }}>
                                                {currentMetrics.isFromTest ? 
                                                    `BANDWIDTH TEST ${currentMetrics.dataAge ? `(${currentMetrics.dataAge}m ago)` : ''}` : 
                                                    'LIVE MONITORING'
                                                }
                                            </Typography>
                                        </div>
                                        <div className="relative">
                                            <svg width="180" height="100" viewBox="0 0 180 100" className="mx-auto">
                                                <path
                                                    d="M 20 80 A 70 70 0 0 1 160 80"
                                                    stroke="#e0e0e0"
                                                    strokeWidth="8"
                                                    fill="none"
                                                />
                                                <path
                                                    d="M 20 80 A 70 70 0 0 1 160 80"
                                                    stroke="#673ab7"
                                                    strokeWidth="8"
                                                    fill="none"
                                                    strokeDasharray={`${Math.min(100, (animatedMetrics.averageBandwidth / 20000) * 100) * 2.2} 220`}
                                                    style={{ transition: 'stroke-dasharray 0.1s ease' }}
                                                />
                                            </svg>
                                            <div className="absolute inset-0 flex items-center justify-center" style={{ top: '0px' }}>
                                                <div>
                                                    <Typography variant="h4" className="text-purple font-bold">
                                                        {Math.round(animatedMetrics.averageBandwidth || animatedMetrics.totalBandwidth || 0).toLocaleString()}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        MB/s
                                                    </Typography>
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                </Grid>

                                {/* Secondary Metrics - STREAM Benchmark Results */}
                                <Grid item xs={12} md={6}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Card className="p-12 text-center" style={{ minHeight: '90px' }}>
                                                <Typography variant="h5" className="text-blue font-bold">
                                                    {Math.round(animatedMetrics.addBandwidth || 0).toLocaleString()}
                                                </Typography>
                                                <Typography variant="caption">Add (MB/s)</Typography>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                                    <div 
                                                        className="bg-blue-500 h-2 rounded-full transition-all duration-100"
                                                        style={{ width: `${Math.min(100, (animatedMetrics.addBandwidth / 15000) * 100)}%` }}
                                                    ></div>
                                                </div>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Card className="p-12 text-center" style={{ minHeight: '90px' }}>
                                                <Typography variant="h5" className="text-green font-bold">
                                                    {Math.round(animatedMetrics.copyBandwidth || 0).toLocaleString()}
                                                </Typography>
                                                <Typography variant="caption">Copy (MB/s)</Typography>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                                    <div 
                                                        className="bg-green-500 h-2 rounded-full transition-all duration-100"
                                                        style={{ width: `${Math.min(100, (animatedMetrics.copyBandwidth / 20000) * 100)}%` }}
                                                    ></div>
                                                </div>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Card className="p-12 text-center" style={{ minHeight: '90px' }}>
                                                <Typography variant="h5" className="text-orange font-bold">
                                                    {Math.round(animatedMetrics.scaleBandwidth || 0).toLocaleString()}
                                                </Typography>
                                                <Typography variant="caption">Scale (MB/s)</Typography>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                                    <div 
                                                        className="bg-orange-500 h-2 rounded-full transition-all duration-100"
                                                        style={{ width: `${Math.min(100, (animatedMetrics.scaleBandwidth / 15000) * 100)}%` }}
                                                    ></div>
                                                </div>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Card className="p-12 text-center" style={{ minHeight: '90px' }}>
                                                <Typography variant="h5" className="text-cyan font-bold">
                                                    {Math.round(animatedMetrics.triadBandwidth || 0).toLocaleString()}
                                                </Typography>
                                                <Typography variant="caption">Triad (MB/s)</Typography>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                                    <div 
                                                        className="bg-cyan-500 h-2 rounded-full transition-all duration-100"
                                                        style={{ width: `${Math.min(100, (animatedMetrics.triadBandwidth / 15000) * 100)}%` }}
                                                    ></div>
                                                </div>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Error Display */}
                    {stressTestError && (
                        <Grid item xs={12}>
                            <Paper className="p-16 bg-red-50">
                                <Typography variant="body2" className={classes.errorText}>
                                    <Icon className="mr-8">error</Icon>
                                    {stressTestError}
                                </Typography>
                            </Paper>
                        </Grid>
                    )}

                    {/* Connection Status Indicator */}
                    {runningTests.size > 0 && connectionStatus !== 'connected' && (
                        <Grid item xs={12}>
                            <Paper className={`p-16 ${connectionStatus === 'degraded' ? 'bg-yellow-50' : 'bg-red-50'}`}>
                                <Typography variant="body2" className="flex items-center">
                                    <Icon className="mr-8" color={connectionStatus === 'degraded' ? 'warning' : 'error'}>
                                        {connectionStatus === 'degraded' ? 'warning' : 'signal_wifi_off'}
                                    </Icon>
                                    {connectionStatus === 'degraded' ? 
                                        `Connection issues detected (${pollingErrors} failed attempts). Using reduced refresh rate.` :
                                        `Connection failed (${pollingErrors} errors). Real-time updates unavailable.`
                                    }
                                </Typography>
                            </Paper>
                        </Grid>
                    )}
                </Grid>

                {stressTestLoading && <LinearProgress className="mt-16" />}
            </DialogContent>

            {/* Action Buttons */}
            <div style={{ padding: '16px 24px 24px', borderTop: '1px solid #e0e0e0', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <Button
                    onClick={handleManualRefresh}
                    color="default"
                    disabled={stressTestLoading}
                    startIcon={stressTestLoading ? <LinearProgress className="w-20" /> : <Icon>refresh</Icon>}
                    size="small"
                >
                    Refresh Data
                </Button>
                
                <Button
                    onClick={handleExecuteTest}
                    color="primary"
                    variant="contained"
                    disabled={!selectedNode || stressTestLoading || runningTests.has(selectedNode)}
                    startIcon={runningTests.has(selectedNode) ? <LinearProgress className="w-20" /> : <Icon>play_arrow</Icon>}
                >
                    {runningTests.has(selectedNode) ? 
                        `Test Running on ${selectedNode}` : 
                        'Start Memory Bandwidth Test'
                    }
                </Button>
            </div>
        </Dialog>
    );
};

export default MemoryBandwidthDialog;
